import React, { useState, useEffect, useRef } from 'react';
import api from '@/api';
import { useCurrency } from '@/hooks/useCurrency';
import { toast } from 'sonner';
import { formatDate } from '@/utils/datetime';
import { useNavigate } from 'react-router-dom';
import { PaperPlaneRight, DownloadSimple, Upload, CheckCircle, Clock, FileXls, Warning } from '@phosphor-icons/react';

const ROUNDS = ['Friends & Family', 'Pre-Seed', 'Seed', 'Pre-Series A', 'Series A', 'Series B', 'Series C', 'Series D+'];

const VCInvite = ({ user }) => {
  const { formatCompact } = useCurrency();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [invites, setInvites] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [managedFunds, setManagedFunds] = useState([]);
  const fileRef = useRef(null);

  // Form state
  const [email, setEmail] = useState('');
  const [managedFundId, setManagedFundId] = useState('');
  const [investmentAmount, setInvestmentAmount] = useState('');
  const [investmentYear, setInvestmentYear] = useState(new Date().getFullYear());
  const [round, setRound] = useState('Seed');
  const [roundSize, setRoundSize] = useState('');
  const [preMoney, setPreMoney] = useState('');
  const [postMoney, setPostMoney] = useState('');
  const [leadInvestor, setLeadInvestor] = useState(false);
  const [coInvestors, setCoInvestors] = useState('');
  const [personalNote, setPersonalNote] = useState('');

  useEffect(() => { fetchInvites(); fetchManagedFunds(); }, []);

  // Auto-calc post money
  useEffect(() => {
    if (preMoney && roundSize) {
      setPostMoney(String(parseFloat(preMoney) + parseFloat(roundSize)));
    }
  }, [preMoney, roundSize]);

  const fetchInvites = async () => {
    try {
      const res = await api.get('/vc/invites');
      setInvites(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchManagedFunds = async () => {
    try {
      const res = await api.get('/vc/managed-funds');
      setManagedFunds(res.data);
      if (res.data.length > 0 && !managedFundId) setManagedFundId(res.data[0].id);
    } catch (err) { console.error(err); }
  };

  const handleSendInvite = async () => {
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    if (managedFunds.length > 0 && !managedFundId) { toast.error('Please select which fund this investment is from'); return; }
    setLoading(true);
    try {
      const payload = {
        email,
        personal_note: personalNote || null,
        managed_fund_id: managedFundId || null,
        investment_amount: investmentAmount ? parseFloat(investmentAmount) : null,
        investment_year: investmentYear,
        round,
        round_size: roundSize ? parseFloat(roundSize) : null,
        pre_money_valuation: preMoney ? parseFloat(preMoney) : null,
        post_money_valuation: postMoney ? parseFloat(postMoney) : null,
        lead_investor: leadInvestor,
        co_investors: coInvestors,
      };
      await api.post('/vc/invite', payload);
      toast.success('Invite sent!');
      setEmail(''); setInvestmentAmount(''); setRoundSize(''); setPreMoney(''); setPostMoney(''); setCoInvestors(''); setPersonalNote('');
      fetchInvites();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/vc/invite/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', 'Founderboard_Invite_Template.xlsx');
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('Template downloaded!');
    } catch (err) { toast.error('Failed to download template'); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      const res = await api.post('/vc/invite/bulk-upload', formData);
      toast.success(`${res.data.invites_created} invites created!`);
      fetchInvites();
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleRetract = async (inviteId) => {
    if (!confirm('Retract this invite?')) return;
    try {
      await api.delete(`/vc/invite/${inviteId}`);
      toast.success('Invite retracted');
      fetchInvites();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleResend = async (inviteId) => {
    try {
      await api.post(`/vc/invite/${inviteId}/resend`, {});
      toast.success('Invite resent!');
      fetchInvites();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const realInvites = invites.filter(i => i.invited_email && !i.invited_email.includes('@placeholder'));

  const equityPct = investmentAmount && postMoney && parseFloat(postMoney) > 0
    ? ((parseFloat(investmentAmount) / parseFloat(postMoney)) * 100).toFixed(2)
    : null;

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-heading font-bold text-text-primary mb-2" data-testid="invite-heading">Invite Portfolio Companies</h1>
        <p className="text-text-secondary mb-6">Send invites with investment details for MoIC tracking</p>

        {managedFunds.length === 0 && (
          <div className="mb-6 bg-warning/10 border border-warning/30 rounded-lg p-4 flex items-start gap-3" data-testid="no-funds-banner">
            <Warning size={20} weight="fill" className="text-warning flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-medium text-text-primary mb-1">Add at least one fund to send investment-tagged invites</div>
              <div className="text-xs text-text-secondary mb-2">Each investment must be tagged to a specific fund (e.g., "Climate Fund II — 2024"). Manage your funds from the Profile page.</div>
              <button onClick={() => navigate('/vc/profile')} className="text-xs font-medium text-warning underline hover:text-warning/80" data-testid="go-to-profile-btn">Go to Profile →</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Invite Form */}
          <div className="lg:col-span-2 bg-surface border border-border rounded-[10px] p-6" data-testid="invite-form">
            <h2 className="font-heading font-bold text-lg text-text-primary mb-4">New Invite</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Founder's Email *</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="founder@startup.com"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="invite-email" />
              </div>

              <div className="md:col-span-2">
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">From Fund {managedFunds.length > 0 && '*'}</label>
                <select value={managedFundId} onChange={e => setManagedFundId(e.target.value)} disabled={managedFunds.length === 0}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary disabled:opacity-50" data-testid="invite-managed-fund">
                  {managedFunds.length === 0 ? (
                    <option value="">No funds — add one in Profile first</option>
                  ) : (
                    <>
                      <option value="">Select fund...</option>
                      {managedFunds.map(f => (
                        <option key={f.id} value={f.id}>
                          {f.name} — {f.vintage_year}{f.fund_size_cr ? ` — ${formatCompact(f.fund_size_cr * 1e7)}` : ''}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Your Investment (Rs.)</label>
                <input type="number" value={investmentAmount} onChange={e => setInvestmentAmount(e.target.value)} placeholder="5000000"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary font-mono" data-testid="invite-amount" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Year</label>
                <input type="number" value={investmentYear} onChange={e => setInvestmentYear(parseInt(e.target.value))} min={2010} max={2030}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary font-mono" data-testid="invite-year" />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Round</label>
                <select value={round} onChange={e => setRound(e.target.value)}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="invite-round">
                  {ROUNDS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Round Size (Rs.)</label>
                <input type="number" value={roundSize} onChange={e => setRoundSize(e.target.value)} placeholder="20000000"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary font-mono" data-testid="invite-round-size" />
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Pre-Money Valuation (Rs.)</label>
                <input type="number" value={preMoney} onChange={e => setPreMoney(e.target.value)} placeholder="15000000"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary font-mono" data-testid="invite-pre-money" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Post-Money Valuation (Rs.)</label>
                <input type="number" value={postMoney} onChange={e => setPostMoney(e.target.value)} placeholder="Auto-calculated"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary font-mono" data-testid="invite-post-money" />
              </div>

              {equityPct && (
                <div className="md:col-span-2 bg-primary/5 border border-primary/20 rounded-lg px-4 py-3">
                  <span className="text-xs text-text-muted">Equity Held (Auto): </span>
                  <span className="font-mono font-semibold text-primary text-lg" data-testid="equity-pct">{equityPct}%</span>
                </div>
              )}

              <div className="flex items-center gap-3">
                <label className="block text-[11px] uppercase tracking-wider text-text-muted">Lead Investor?</label>
                <button onClick={() => setLeadInvestor(!leadInvestor)}
                  className={`relative w-[44px] h-[24px] rounded-full transition-colors ${leadInvestor ? 'bg-primary' : 'bg-border'}`} data-testid="invite-lead">
                  <div className={`absolute top-[2px] w-[20px] h-[20px] bg-white rounded-full transition-transform ${leadInvestor ? 'translate-x-[22px]' : 'translate-x-[2px]'}`} />
                </button>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Co-Investors</label>
                <input type="text" value={coInvestors} onChange={e => setCoInvestors(e.target.value)} placeholder="Sequoia, Accel"
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" data-testid="invite-co-investors" />
              </div>

              <div className="md:col-span-2">
                <label className="block text-[11px] uppercase tracking-wider text-text-muted mb-1.5">Personal Note</label>
                <textarea value={personalNote} onChange={e => setPersonalNote(e.target.value)} rows={2} placeholder="Looking forward to working together..."
                  className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary" />
              </div>

              <div className="md:col-span-2">
                <button onClick={handleSendInvite} disabled={loading || !email}
                  className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50" data-testid="send-invite-btn">
                  {loading ? 'Sending...' : 'Send Invite'}
                </button>
              </div>
            </div>
          </div>

          {/* Excel Bulk + Recent */}
          <div className="space-y-6">
            <div className="bg-surface border border-border rounded-[10px] p-6" data-testid="bulk-section">
              <div className="flex items-center gap-2 mb-3">
                <FileXls size={20} className="text-success" weight="bold" />
                <h3 className="font-heading font-semibold text-text-primary">Bulk Import</h3>
              </div>
              <p className="text-xs text-text-secondary mb-4">Download the template, fill in multiple portfolio companies, and upload.</p>
              <button onClick={handleDownloadTemplate} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-background border border-border rounded-lg text-sm text-text-primary hover:border-primary transition-colors mb-3" data-testid="download-template-btn">
                <DownloadSimple size={16} weight="bold" />
                Download Template
              </button>
              <label className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary/10 border border-primary/20 rounded-lg text-sm text-primary hover:bg-primary/20 transition-colors cursor-pointer ${uploading ? 'opacity-50' : ''}`}>
                <Upload size={16} weight="bold" />
                {uploading ? 'Uploading...' : 'Upload Filled Template'}
                <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleBulkUpload} className="hidden" disabled={uploading} data-testid="bulk-upload-input" />
              </label>
            </div>
          </div>
        </div>

        {/* Sent Invites List */}
        <div className="bg-surface border border-border rounded-[10px] overflow-hidden" data-testid="invites-list">
          <div className="p-4 border-b border-border">
            <h3 className="font-heading font-bold text-lg text-text-primary">Sent Invites ({realInvites.length})</h3>
          </div>
          {realInvites.length === 0 ? (
            <div className="p-12 text-center text-text-muted">No invites sent yet</div>
          ) : (
            <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
              {realInvites.map(inv => (
                <div key={inv.id} className="p-4 flex items-center gap-4" data-testid={`invite-row-${inv.id}`}>
                  <div className={`w-2 h-2 rounded-full ${inv.status === 'accepted' ? 'bg-success' : inv.status === 'declined' ? 'bg-danger' : 'bg-warning'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">{inv.invited_email}</div>
                    <div className="text-[11px] text-text-muted">
                      {inv.round && <span className="mr-2">{inv.round}</span>}
                      {inv.investment_amount && <span className="mr-2 font-mono">{formatCompact(inv.investment_amount)}</span>}
                      {inv.investment_year && <span className="mr-2">{inv.investment_year}</span>}
                      · Sent {formatDate(inv.created_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {inv.status === 'pending' && (
                      <>
                        <button onClick={() => handleResend(inv.id)} className="text-xs px-2.5 py-1 text-info border border-info/30 rounded-lg hover:bg-info/10 transition-colors" data-testid={`resend-${inv.id}`}>Resend</button>
                        <button onClick={() => handleRetract(inv.id)} className="text-xs px-2.5 py-1 text-danger border border-danger/30 rounded-lg hover:bg-danger/10 transition-colors" data-testid={`retract-${inv.id}`}>Retract</button>
                      </>
                    )}
                    <span className={`text-xs px-2 py-1 rounded-full ${inv.status === 'accepted' ? 'bg-success/10 text-success' : inv.status === 'declined' ? 'bg-danger/10 text-danger' : 'bg-warning/10 text-warning'}`}>
                      {inv.status === 'accepted' ? 'Accepted' : inv.status === 'declined' ? 'Declined' : 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VCInvite;
